
import { debounce } from "lodash";
import { enqueueSnackbar } from "notistack";
import { useMemo, useState, useCallback } from "react";

import { Stack } from "@mui/system";
import { Button, TextField, Typography, Autocomplete } from "@mui/material";

import { useAuthContext } from "src/hooks/auth/use-auth-context";

import { useTranslate } from "src/locales";
import { EntityLinkType } from "src/services/common.types";
import { IContactListItem, ContactLinkEntityType } from "src/services/contacts/contacts.types";
import { useGetCompanyContactLinksQuery } from "src/services/connections/connections.service";
import { useAddContactLinkMutation, useGetOrganizationContactsLeanQuery } from "src/services/contacts/contacts.service";
import { PICKER_PAGE, pickerSortConfig, pickerHelperText, pickerNoOptionsText, buildPickerColumnFilters } from "src/utils/picker-search";

import Iconify from "src/components/iconify";
import { ConfirmDialog } from "src/components/custom-dialog";

type Props = {
  open: boolean;
  onClose: () => void;
  onAddContact: () => void;
  profileId: string;
}

type PickableContact = Pick<IContactListItem, 'id' | 'first_name' | 'last_name'>;

// Every link of the company, so contacts linked beyond the first few still show as already linked.
const ALL_LINKS_PAGE_OPTIONS = {
  page: 1,
  per_page: 100
};

export const ContactEditorDialog = ({
  open,
  onClose,
  onAddContact,
  profileId
}: Props) => {

  const { t } = useTranslate();

  const authContext = useAuthContext();

  const [contactSearchQuery, setContactSearchQuery] = useState("");
  const [selectedContact, setSelectedContact] = useState<PickableContact | null>(null);

  const [addContactLink, { isLoading: isAddingContactLinks }] = useAddContactLinkMutation();

  const { data: contactLinks } = useGetCompanyContactLinksQuery({
    profile_id: profileId,
    params: ALL_LINKS_PAGE_OPTIONS
  }, {
    skip: !profileId || !open,
  });

  // Name/email search on the lean list, only while open (JT1-1586).
  const { currentData: contacts, isFetching: isSearchingContacts, isError: isContactSearchError } = useGetOrganizationContactsLeanQuery(
    {
      org_id: authContext.organization.id,
      page: PICKER_PAGE,
      sort_config: pickerSortConfig(contactSearchQuery),
      column_filters: buildPickerColumnFilters(contactSearchQuery, { email: true }),
    },
    {
      skip: !open,
    }
  );

  const onSearchQueryChange = useMemo(
    () => debounce((value: string) => {
      setContactSearchQuery(value);
    }, 500),
    [setContactSearchQuery]
  );

  const onLinkModalClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const linkedContactIds = useMemo(
    () => new Set(contactLinks?.results.map((link) => link.contact.id) ?? []),
    [contactLinks]
  );

  const handleUpdateContactLinks = useCallback(async () => {

    if (!selectedContact) return;

    try {
      await addContactLink({
        data: {
          entity_id: profileId,
          entity_type: ContactLinkEntityType.COMPANY_PROFILE,
          contacts: [{
            type: EntityLinkType.OTHER,
            id: selectedContact.id,
          }]
        }
      }).unwrap();

      onLinkModalClose();

      enqueueSnackbar(t('companies.profile.api.add_contact_link.success'), { variant: 'success' });
    }
    catch (error) {
      enqueueSnackbar(t('companies.profile.api.add_contact_link.default_error'), { variant: 'error' });
    }
  }, [onLinkModalClose, profileId, selectedContact, addContactLink, t]);

  return (
    <ConfirmDialog
      open={open}
      title={
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography variant="h6">
            {t('companies.profile.linked_contacts.editor_title')}
          </Typography>

          <Button
            variant="outlined"
            startIcon={<Iconify icon="eva:plus-fill" />}
            onClick={onAddContact}
          >
            {t('common.create')}
          </Button>

        </Stack>
      }
      onClose={onLinkModalClose}
      content={
        <Autocomplete
          sx={{
            paddingTop: 2,
          }}
          multiple={false}
          options={(contacts?.results ?? []) as PickableContact[]}
          value={selectedContact}
          // The server already matched the text; MUI's default label filter would drop its results.
          filterOptions={(options) => options}
          loading={isSearchingContacts}
          noOptionsText={pickerNoOptionsText(t, isContactSearchError)}
          // Shown disabled rather than hidden, so a search never silently shrinks to nothing.
          getOptionDisabled={(option) => linkedContactIds.has(option.id)}
          // 'reset' is MUI re-syncing the input after a selection or re-render, not the user typing.
          onInputChange={(_e, value, reason) => reason !== 'reset' && onSearchQueryChange(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t(
                'companies.profile.primary_contact.search'
              )}
              placeholder={t(
                'companies.profile.primary_contact.search_placeholder'
              )}
              helperText={pickerHelperText(t, contacts)}
            />
          )}
          onChange={(_e, value) => setSelectedContact(value)}
          renderOption={({ key, ...rest }, option) => (
            <li key={key} {...rest}>
              <Stack direction="column">
                <Typography variant="body1">
                  {`${option.first_name} ${option.last_name}`}
                </Typography>
                {linkedContactIds.has(option.id) && (
                  <Typography variant="caption" color="text.secondary">
                    {t('common.picker.already_linked')}
                  </Typography>
                )}
              </Stack>
            </li>
          )}
          getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          getOptionKey={(option) => option.id}
        />
      }
      action={
        <Button variant="contained" disabled={!selectedContact || isAddingContactLinks} onClick={handleUpdateContactLinks}>
          {t('common.link')}
        </Button>
      }
    />
  )
}
